import React, { useState } from 'react';

import { useNavigate, useParams } from 'react-router-dom';

import CourseForm from '../../../components/forms/CourseForm';
import AdminLayout from '../../../components/layout/AdminLayout';
import { useCourseById, useUpdateCourse } from '../../../hooks/useCourseApi';
import { useFileApi } from '../../../hooks/useFileApi';
import { CourseDTO } from '../../../services/apiClient';

const CourseEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { data: entityData } = useCourseById(id!);
  const updateCourse = useUpdateCourse();
  const { uploadFile } = useFileApi();
  const navigate = useNavigate();

  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [headerImageFile, setHeaderImageFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const breadcrumbs = [
    { label: 'Admin', href: '#' },
    { label: 'Courses', href: '/admin/courses' },
    { label: 'Edit', href: '#' },
  ];

  const handleSubmit = async (data: CourseDTO) => {
    try {
      setIsSubmitting(true);

      // One shared "files" container, one folder per purpose. This matches the live production
      // storage account, which already holds "files/course-logos/" and "files/course-headers/".
      //
      // "files" was never declared in BlobContainerConstants, and FileFunction rejects an unknown
      // container name outright, so every upload from this screen came back 400 and no course ever
      // got an image. Declaring it is the fix.
      if (logoFile) {
        const uploadResponse = await uploadFile(
          'files',
          logoFile.name,
          logoFile,
          'course-logos',
        );
        if (uploadResponse && uploadResponse.success) {
          data.logoFileId = uploadResponse.data;
        }
      }

      if (headerImageFile) {
        const uploadResponse = await uploadFile(
          'files',
          headerImageFile.name,
          headerImageFile,
          'course-headers',
        );
        if (uploadResponse && uploadResponse.success) {
          data.headerImageFileId = uploadResponse.data;
        }
      }

      await updateCourse.mutateAsync({ id: id!, data });
      navigate('/admin/courses');
    } catch (error) {
      console.error('Error updating Course:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AdminLayout
      activeMenuItem="/admin/courses"
      breadcrumbs={breadcrumbs}
      allowedRoles={['Admin']}
      requiredPermission="Settings.Course.Edit"
    >
      {entityData ? (
        <CourseForm
          initialValues={entityData}
          onSubmit={handleSubmit}
          onCancel={() => navigate('/admin/courses')}
          setLogoFile={setLogoFile}
          setHeaderImageFile={setHeaderImageFile}
          isLoading={isSubmitting}
        />
      ) : (
        <p>Loading...</p>
      )}
    </AdminLayout>
  );
};

export default CourseEdit;
